import type { MigrationConflictCheckResponse } from '../../types';
import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

const FAIL_OPEN_RESULT: MigrationConflictCheckResponse = {
  hasConflict: false,
  conflictingMigrations: []
};

const checkMigrationConflicts = async (
  bearerToken: string,
  targetOrgId: string,
  excludeMigrationId?: string,
  signal?: AbortSignal
): Promise<MigrationConflictCheckResponse> => {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const params = new URLSearchParams({ targetOrgId });
    if (excludeMigrationId) {
      params.set('excludeMigrationId', excludeMigrationId);
    }
    const url = `${baseUrl}/api/migrations/check-conflicts?${params.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(bearerToken),
      ...(signal !== undefined && { signal })
    });

    if (!response.ok) {
      logger.warn('check-conflicts returned non-200; failing open', {
        status: response.status,
        targetOrgId
      });
      return FAIL_OPEN_RESULT;
    }

    const data: MigrationConflictCheckResponse = await response.json() as MigrationConflictCheckResponse;
    logger.info('Conflict check complete', {
      targetOrgId,
      hasConflict: data.hasConflict,
      count: data.conflictingMigrations.length
    });
    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      logger.info('Conflict check request aborted', { targetOrgId });
    } else {
      const msg = error instanceof Error ? error.message : String(error);
      logger.warn('Conflict check failed; failing open', { error: msg, targetOrgId });
    }
    return FAIL_OPEN_RESULT;
  }
};

export default checkMigrationConflicts;
